import { ANNUALLY } from '@constants/billingPeriods'
import { PLN } from '@constants/currencies'
import { INVOICE, PRZELEWY24 } from '@constants/paymentMethods'
import { formatNumericBasedOnLanguage } from '@helpers/data'
import { isAvailablePlan } from '@pages/Checkout/helpers/isAvailablePlan'
import { isUnavailablePlan } from '@pages/Checkout/helpers/isUnavailablePlan'
import { getFormattedExtensionDate } from '@pages/Checkout/helpers/payment'
import { useGetPaymentButton } from '@pages/Checkout/hooks/useGetPaymentButton'
import { visits } from '@pages/Checkout/hooks/useGetPlanLimits'
import { useGetPurchaseSummaryInfo } from '@pages/Checkout/hooks/useGetPurchaseSummaryInfo'
import { SaveAmountOnBadge } from '@pages/Checkout/routes/components/SaveAmountOnBadge'
import { useCheckoutContext } from '@pages/Checkout/routes/contexts'
import { PAYMENTS } from '@routes/path'
import { CheckoutRoutesTypes } from '@routes/path/payments'
import {
  Button,
  Divider,
  Heading,
  Icon,
  Panel,
  Paragraph,
  Spacer,
  Spreader
} from '@ui-kit'
import { Fragment } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Column, Row } from 'simple-flexbox'

import { AnnuallyPrice } from './components/AnnuallyPrice'
import { TotalPrice } from './components/TotalPrice'
import { VatAmount } from './components/VatAmount'
import { getMonthlyPrice } from './helpers/getMonthlyPrice'
import { getPromoBadge } from './helpers/getPromoBadge'
import styles from './PurchaseSummaryPanel.module.scss'

export const PurchaseSummaryPanel = () => {
  const { t, i18n } = useTranslation()

  const { language } = i18n

  const {
    goToStep,
    accountCurrency,
    chosenBillingPeriod,
    checkoutDataInContext: { chosenRecordKey },
    checkoutDataInContext,
    formik,
    calculatedExtensionDate
  } = useCheckoutContext()

  const { paymentMethod } = formik.values

  const paymentButton = useGetPaymentButton()

  const { currentStep } = checkoutDataInContext

  const {
    planName,
    planPricePerMonth,
    planPriceInMonthlyPeriod,
    finalPrice,
    vatAmount,
    totalPrice,
    activePromotionKey,
    discount,
    planPricePerMonthWithoutDiscount
  } = useGetPurchaseSummaryInfo()

  const shouldRenderVisits = chosenRecordKey && isAvailablePlan(chosenRecordKey)

  const isPrzelewy24OrInvoice =
    paymentMethod === PRZELEWY24 || paymentMethod === INVOICE

  const isNotSupportedPlan = chosenRecordKey
    ? isUnavailablePlan(chosenRecordKey)
    : false

  const handleContinueToPayment = () => {
    goToStep(PAYMENTS.CHECKOUT.PAYMENT)
  }

  const buttons: Partial<Record<CheckoutRoutesTypes, JSX.Element>> = {
    [PAYMENTS.CHECKOUT.CONFIGURE]: (
      <Button
        onClick={handleContinueToPayment}
        fullWidth
        data-testid='continue-to-payment-button'
      >
        {t('checkout.configure.purchase.summary.continue')}
      </Button>
    ),
    [PAYMENTS.CHECKOUT.PAYMENT]: paymentButton
  }

  return (
    <Column>
      <div className={styles['summary-panel']}>
        <Panel
          className={styles['summary-panel__panel']}
          padding={{ y: 30, x: 0 }}
          surface={2}
        >
          <div className={styles['summary-panel__heading']}>
            <Heading
              level={2}
              weight={600}
              data-testid='purchase-summary-panel-heading'
            >
              {t('checkout.configure.purchase.summary.heading')}
            </Heading>

            <Spacer space={20} />
          </div>

          <div className={styles['summary-panel__content']}>
            <div className={styles['summary-panel__price']}>
              <Column>
                <Paragraph weight={600} size={16}>
                  {t('checkout.configure.purchase.summary.plan.name', {
                    planName
                  })}
                </Paragraph>

                {isNotSupportedPlan && (
                  <Paragraph data-testid='not-supported-plan'>
                    ({t('word.old')})
                  </Paragraph>
                )}

                {shouldRenderVisits && (
                  <Paragraph color='neutral-6'>
                    {t('checkout.configure.purchase.summary.visits', {
                      visits: formatNumericBasedOnLanguage(
                        visits[chosenRecordKey]
                      )
                    })}
                  </Paragraph>
                )}
              </Column>

              {chosenBillingPeriod === ANNUALLY ? (
                <AnnuallyPrice
                  planPricePerMonth={planPricePerMonth}
                  planPriceInMonthlyPeriod={planPriceInMonthlyPeriod}
                  planPricePerMonthWithoutDiscount={
                    planPricePerMonthWithoutDiscount
                  }
                  discount={discount}
                />
              ) : (
                getMonthlyPrice({
                  finalPrice,
                  basicPrice: planPricePerMonth,
                  planPricePerMonthWithoutDiscount,
                  activePromotionKey,
                  discount
                })
              )}
            </div>

            {getPromoBadge({
              activePromotionKey,
              discount,
              currency: accountCurrency
            })}

            {accountCurrency !== PLN && <Spacer space={15} />}

            {accountCurrency === PLN && <VatAmount vatAmount={vatAmount} />}
          </div>

          <div className={styles['summary-panel__summary']}>
            <Divider />

            <Spacer space={15} />

            {chosenBillingPeriod === ANNUALLY && (
              <Fragment>
                <Row justifyContent='flex-end'>
                  <SaveAmountOnBadge />
                </Row>

                <Spacer space={5} />
              </Fragment>
            )}

            <TotalPrice totalPrice={totalPrice} />

            <Spacer space={10} />

            {isPrzelewy24OrInvoice && calculatedExtensionDate ? (
              <Row justifyContent='space-between'>
                <Paragraph size={12} color='neutral-6'>
                  {t('checkout.configure.purchase.summary.extending.to')}
                </Paragraph>

                <Paragraph size={12} color='neutral-6' weight={600}>
                  {getFormattedExtensionDate({
                    language,
                    date: calculatedExtensionDate
                  })}
                </Paragraph>
              </Row>
            ) : null}

            <Spacer space={20} />

            {buttons[currentStep]}
          </div>
        </Panel>

        <Spacer space={30} />

        {isPrzelewy24OrInvoice ? null : (
          <Fragment>
            <Row>
              <Column className={styles['summary-panel__icon-wrapper']}>
                <Icon icon='icon-ok' size={14} />
              </Column>

              <Spreader spread={10} />

              <Paragraph size={12}>
                <Trans i18nKey='checkout.configure.purchase.summary.you.can.cancel' />
              </Paragraph>
            </Row>

            <Spacer space={15} />
          </Fragment>
        )}

        <Row>
          <Icon icon='icon-shield' color='success' size={14} />

          <Spreader spread={10} />

          <Paragraph size={12}>
            {t('checkout.configure.purchase.summary.money.back.guarantee')}
          </Paragraph>
        </Row>
      </div>
    </Column>
  )
}
